// Step 3 of the brand journey: website only in, Martian researches it
// (POST /api/brand/discovery/start -> company_discovery.py), the brand
// reviews/corrects what was found, then continues into the existing
// authenticated product.
//
// Reachable both logged out and logged in -- there is no ProtectedRoute
// wrapper (App.js), the same way SnapshotPage.js/EarlyAccessPage.js are
// public. A logged-out visitor only ever sees the INPUT state; submitting
// there hands the domain to /login exactly the way SnapshotPage.js's own
// handleContinue already does (?url=), and LoginPage.js's
// handleRegisterWithPersona now carries that domain back here
// (?domain=...) after signup, where this page auto-starts the real
// research call the moment `user` becomes truthy -- no second manual
// submit needed.
//
// REVIEW reuses CompanyContextPage.js's own field metadata and editing
// components (FIELD_META/LIST_FIELD_META/EditableTextField/
// EditableListField, exported from that file for exactly this reuse) --
// same data, same edit affordances as the standalone Company Context
// settings page; this screen only adds the onboarding-specific framing
// and the research-progress/Continue steps around them.
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ArrowRight, Check } from '@phosphor-icons/react';
import { ARCState, GLIMState, IntelligenceChain } from '@/brand';
import { toast } from 'sonner';
import { brandDiscoveryAPI, companyContextAPI } from '@/lib/api';
import { FIELD_META, LIST_FIELD_META, EditableTextField, EditableListField } from '@/pages/brand/CompanyContextPage';
import { contextProvenance, saveErrorMessage } from '@/pages/brand/CompanyContextPage';

// Exported for reuse by BrandOnboardingPage.js (the manual "Add a Brand"
// form's own onboarding flow) -- same normalization rule, one definition,
// so a website typed there and later looked up by
// POST /brand/discovery/start's own normalize_website() always agree on
// the same string and correctly reuse the same client instead of racing
// into a second, duplicate one.
export function looksLikeDomain(value) {
  const t = (value || '').trim().replace(/^https?:\/\//i, '').replace(/\/.*$/, '');
  return /^[a-z0-9-]+(\.[a-z0-9-]+)+$/i.test(t) ? t.toLowerCase() : null;
}

// The "lightweight progress" narrative the product spec asks for. This
// is deliberately NOT a claim of real per-step completion while the one
// real research call is in flight -- see the RESEARCHING render below:
// no item here ever gets a checkmark until the actual response has
// returned and genuinely contains evidence for it (rendered instead at
// the top of REVIEW, from real `discovery.fields_found`).

// The eight Company Context fields this review shows (company_context.py
// ALL_FIELDS), and the core ones whose absence the introduction names.
const REVIEW_FIELDS = ['company_name', 'business_summary', 'primary_market', 'markets_served', 'audience', 'positioning', 'growth_priorities', 'additional_context'];
const CORE_FIELDS = ['company_name', 'business_summary', 'primary_market', 'audience', 'positioning'];

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

const RESEARCH_STEPS = [
  'Finding your website',
  'Understanding what you sell',
  'Identifying your audience',
  'Mapping your positioning',
  'Building your growth context',
];

// Maps each research-narrative line to the real field(s) that would back
// it, so REVIEW's checklist reflects what was ACTUALLY found, never a
// fixed/fabricated "5 of 5" claim.
const STEP_EVIDENCE_FIELDS = [
  null, // "Finding your website" -- backed by discovery.pages_checked, not a field
  ['business_summary'],
  ['audience'],
  ['positioning'],
  ['markets_served', 'growth_priorities', 'primary_market'],
];

export default function BrandDiscoveryPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stage, setStage] = useState('input'); // input | researching | review
  const [website, setWebsite] = useState(() => new URLSearchParams(window.location.search).get('domain') || '');
  const [error, setError] = useState('');
  const [result, setResult] = useState(null); // { client, company_context, discovery }
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const autoStarted = useRef(false);

  // Ambient "here's what Martian is doing right now" cycle while the one
  // real research call is in flight. Purely descriptive -- no item is
  // ever marked done here.
  useEffect(() => {
    if (stage !== 'researching') return;
    setActiveStepIndex(0);
    const id = setInterval(() => {
      setActiveStepIndex((i) => (i + 1 < RESEARCH_STEPS.length ? i + 1 : i));
    }, 1400);
    return () => clearInterval(id);
  }, [stage]);

  const runResearch = async (rawWebsite) => {
    setStage('researching');
    setError('');
    try {
      const res = await brandDiscoveryAPI.start(rawWebsite);
      setResult(res.data);
      setStage('review');
    } catch (err) {
      setError(err?.response?.data?.detail || "Martian couldn't research that website — please try again.");
      setStage('input');
    }
  };

  // A domain carried back from /login (LoginPage.js's
  // handleRegisterWithPersona) means the visitor just signed up
  // specifically to have Martian research this website -- auto-start
  // rather than making them submit a second time.
  useEffect(() => {
    if (user && website && stage === 'input' && !autoStarted.current) {
      const clean = looksLikeDomain(website);
      if (clean) {
        autoStarted.current = true;
        runResearch(clean);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const clean = looksLikeDomain(website);
    if (!clean) {
      setError('Enter a valid website, like acme.com');
      return;
    }
    setError('');
    if (!user) {
      // Not signed in yet -- the exact same handoff SnapshotPage.js
      // already uses: the domain survives signup via ?url=, and
      // LoginPage.js carries it into the post-auth redirect back here.
      navigate(`/login?url=${encodeURIComponent(clean)}`);
      return;
    }
    runResearch(clean);
  };

  // Returns false on failure so the field editor / review prompt stays open.
  const saveField = async (key, value) => {
    try {
      const res = await companyContextAPI.update(result.client.id, { [key]: value });
      setResult((r) => ({ ...r, company_context: res.data }));
      return true;
    } catch (err) {
      toast.error(saveErrorMessage(err));
      return false;
    }
  };

  const fieldsFound = result?.discovery?.fields_found || [];
  const pagesChecked = result?.discovery?.pages_checked || 0;
  const context = result?.company_context || {};
  // The same rule FieldReviewHint uses, so every counted field shows a Review action.
  const reviewCount = REVIEW_FIELDS.filter((key) => contextProvenance(context[key]) === 'inferred').length;
  const missingCoreCount = CORE_FIELDS.filter((key) => contextProvenance(context[key]) === 'missing').length;

  return (
    <div className="min-h-screen bg-[var(--warm-canvas)]" data-testid="brand-discovery-page">
      <header className="border-b border-[var(--warm-border)] px-6 sm:px-10 py-5">
        <Link to="/" className="font-display text-[16px] font-extrabold text-[var(--text-primary)]" data-testid="brand-discovery-logo">
          MARTIAN
        </Link>
      </header>

      <main className="max-w-lg mx-auto px-6 sm:px-10 py-16 sm:py-24" data-testid={`brand-discovery-${stage}`}>
        {stage === 'input' && (
          <>
            <p className="text-[12px] font-mono uppercase tracking-[0.14em] font-semibold text-[var(--accent)] mb-4">
              Get started
            </p>
            <h1 className="font-display font-extrabold text-[var(--text-primary)] text-balance" style={{ fontSize: 'clamp(1.7rem, 1.4rem + 1.4vw, 2.4rem)', lineHeight: 1.18 }}>
              Tell Martian where to look.
            </h1>
            <p className="mt-4 text-[14.5px] text-[var(--text-secondary)] leading-relaxed">
              Enter your website. Martian will research your brand and build your starting context.
            </p>

            <form onSubmit={handleSubmit} className="mt-9 space-y-4" data-testid="brand-discovery-form">
              <div>
                <label htmlFor="bd-website" className="block text-[12px] font-semibold text-[var(--text-secondary)] mb-1.5">
                  Website
                </label>
                <input
                  id="bd-website" data-testid="brand-discovery-website-input" type="text"
                  value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="yourbrand.com"
                  className="w-full border border-[var(--border)] rounded-[var(--radius-button)] px-4 py-3 text-[15px] text-[var(--text-primary)] bg-[var(--surface)] outline-none focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent-light)]"
                />
              </div>

              {error && <p className="text-[13px] text-[var(--error)]" data-testid="brand-discovery-error">{error}</p>}

              <button
                type="submit" data-testid="brand-discovery-submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-[var(--text-primary)] hover:bg-[var(--text-secondary)] text-white font-semibold text-[15px] px-6 py-3.5 rounded-[var(--radius-button)] transition-colors"
              >
                Let Martian research <ArrowRight size={16} weight="bold" />
              </button>
            </form>
          </>
        )}

        {stage === 'researching' && (
          <div data-testid="brand-discovery-researching-panel">
            <p className="text-[12px] font-mono uppercase tracking-[0.14em] font-semibold text-[var(--accent)] mb-4">
              Martian is researching
            </p>
            <h1 className="font-display font-extrabold text-[var(--text-primary)] text-balance" style={{ fontSize: 'clamp(1.7rem, 1.4rem + 1.4vw, 2.4rem)', lineHeight: 1.18 }}>
              Understanding your brand…
            </h1>
            <p className="mt-3 text-[13.5px] text-[var(--text-tertiary)]">This can take up to a minute.</p>

            <ul className="mt-8 space-y-3" data-testid="brand-discovery-progress">
              {RESEARCH_STEPS.map((label, i) => (
                <li
                  key={label}
                  data-testid={`brand-discovery-progress-step${i === activeStepIndex ? '-active' : ''}`}
                  className="flex items-center gap-3 text-[14px]"
                >
                  <span
                    className={`w-2 h-2 rounded-full shrink-0 ${i === activeStepIndex ? 'bg-[var(--accent)] animate-pulse' : 'bg-[var(--border)]'}`}
                    aria-hidden="true"
                  />
                  <span className={i === activeStepIndex ? 'text-[var(--text-primary)] font-medium' : 'text-[var(--text-tertiary)]'}>
                    {label}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {stage === 'review' && result && (
          <div data-testid="brand-discovery-review-panel">
            <p className="text-[12px] font-mono uppercase tracking-[0.14em] font-semibold text-[var(--accent)] mb-4">
              {pagesChecked > 0 ? 'Martian found this' : 'Your starting context'}
            </p>
            <h1 className="font-display font-extrabold text-[var(--text-primary)] text-balance" style={{ fontSize: 'clamp(1.6rem, 1.3rem + 1.2vw, 2.1rem)', lineHeight: 1.18 }}>
              Here's your starting Company Context.
            </h1>
            <p className="mt-3 text-[14px] text-[var(--text-secondary)] leading-relaxed">
              Edit anything that's not quite right — nothing here is final until you say so.
            </p>

            <div className="mt-6 flex items-start gap-3 rounded-[var(--radius-card)] border border-[var(--accent-border)] bg-[var(--accent-light)] p-4" data-testid="brand-discovery-review-guidance">
              <GLIMState state="curious" size={44} showLabel={false} announce={false} />
              <div className="min-w-0">
                <p className="text-[13.5px] font-semibold text-[var(--text-primary)]">Check these details before relying on them.</p>
                <p className="mt-1 text-[12.5px] leading-relaxed text-[var(--text-secondary)]" data-testid="brand-discovery-review-summary">
                  {pagesChecked > 0
                    ? `Martian researched ${plural(pagesChecked, 'page', 'pages')} of your public website.`
                    : "Martian couldn't reach your website, so it had no public pages to research."}
                  {' '}{reviewCount > 0
                    ? `${plural(reviewCount, 'detail below was', 'details below were')} inferred and ${reviewCount === 1 ? 'needs' : 'need'} your confirmation — open Review on each to confirm or correct it.`
                    : 'Nothing below is waiting for your confirmation.'}
                  {' '}{missingCoreCount > 0
                    ? `${plural(missingCoreCount, 'core detail is', 'core details are')} missing; add ${missingCoreCount === 1 ? 'it' : 'them'} if relevant.`
                    : 'Every core detail has a value.'}
                  {' '}Martian will not fill a gap with a guess.
                </p>
              </div>
            </div>

            <ul className="mt-6 space-y-2" data-testid="brand-discovery-found-summary">
              <li className="flex items-center gap-2 text-[13px] text-[var(--text-secondary)]" data-testid="brand-discovery-found-website">
                <Check size={14} weight="bold" className={pagesChecked > 0 ? 'text-[var(--success)]' : 'text-[var(--text-tertiary)]'} />
                {pagesChecked > 0 ? `Checked ${pagesChecked} page${pagesChecked === 1 ? '' : 's'} of your website` : "Couldn't reach your website"}
              </li>
              {RESEARCH_STEPS.slice(1).map((label, idx) => {
                const evidenceFields = STEP_EVIDENCE_FIELDS[idx + 1] || [];
                const found = evidenceFields.some((f) => fieldsFound.includes(f));
                return (
                  <li key={label} className="flex items-center gap-2 text-[13px] text-[var(--text-secondary)]" data-testid={`brand-discovery-found-${idx}`}>
                    <Check size={14} weight="bold" className={found ? 'text-[var(--success)]' : 'text-[var(--border-strong,var(--border))]'} />
                    {found ? label : `${label} — not enough public information`}
                  </li>
                );
              })}
            </ul>

            <div className="mt-8 rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] shadow-[var(--shadow-card)] px-5 sm:px-6" data-testid="brand-discovery-context-form">
              {/* FIELD_META[5] remains appended for compatibility with the established editor. */}
              <EditableTextField meta={FIELD_META[5]} field={result.company_context.company_name} onSave={saveField} reviewHint={FIELD_META[5].guidance} onReviewConfirm={saveField} />
              <EditableTextField meta={FIELD_META[0]} field={result.company_context.business_summary} onSave={saveField} reviewHint={FIELD_META[0].guidance} onReviewConfirm={saveField} />
              <EditableTextField meta={FIELD_META[1]} field={result.company_context.primary_market} onSave={saveField} reviewHint={FIELD_META[1].guidance} onReviewConfirm={saveField} />
              <EditableListField meta={LIST_FIELD_META[0]} field={result.company_context.markets_served} onSave={saveField} reviewHint={LIST_FIELD_META[0].guidance} onReviewConfirm={saveField} />
              <EditableTextField meta={FIELD_META[2]} field={result.company_context.audience} onSave={saveField} reviewHint={FIELD_META[2].guidance} onReviewConfirm={saveField} />
              <EditableTextField meta={FIELD_META[3]} field={result.company_context.positioning} onSave={saveField} reviewHint={FIELD_META[3].guidance} onReviewConfirm={saveField} />
              <EditableListField meta={LIST_FIELD_META[1]} field={result.company_context.growth_priorities} onSave={saveField} reviewHint={LIST_FIELD_META[1].guidance} onReviewConfirm={saveField} />
              <EditableTextField meta={FIELD_META[4]} field={result.company_context.additional_context} onSave={saveField} reviewHint={FIELD_META[4].guidance} onReviewConfirm={saveField} />
            </div>

            <section className="mt-8 rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] p-4 sm:p-5" data-testid="brand-discovery-arc-context">
              <div className="flex items-start gap-3">
                <ARCState state="thinking" size={44} showLabel label="Reasoning flow" announce={false} className="shrink-0" />
                <div className="min-w-0">
                  <p className="text-[12px] font-mono uppercase tracking-[0.08em] text-[var(--text-tertiary)]">Why this matters</p>
                  <h2 className="mt-1 font-display text-[17px] font-semibold text-[var(--text-primary)]">Your business context is the starting point.</h2>
                  <p className="mt-1 text-[12.5px] leading-relaxed text-[var(--text-secondary)]">Martian uses these details to ground the work it prepares, the decisions it brings you and the questions it suggests for your watchlist. This chain describes Martian's reasoning model; it does not mean every stage already has data for your workspace.</p>
                </div>
              </div>
              <div className="arc-chain-host mt-4 border-t border-[var(--border)] pt-4" data-testid="brand-discovery-chain-host">
                <IntelligenceChain compact label="ARC reasoning model" />
              </div>
            </section>

            <button
              onClick={() => navigate('/')}
              data-testid="brand-discovery-continue"
              className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-[var(--text-primary)] hover:bg-[var(--text-secondary)] text-white font-semibold text-[15px] px-6 py-3.5 rounded-[var(--radius-button)] transition-colors"
            >
              Continue to your Briefing <ArrowRight size={16} weight="bold" />
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
